/* eslint-disable react/prop-types */
/**
 * RestroPulse POS · Till dialogs
 * ────────────────────────────────────────────────────────────────────
 * Single till-open prompt for every cash-eligible role. Steward and
 * bartender never see this — their role does not carry the `settle`
 * permission.
 *
 * POS Cleanup Pass 2 · The former `ManagerCashDecisionPrompt`
 * ("handling cash today?") and the legacy `TillCloseDialog` /
 * `TillCloseSummaryCard` have been removed. Till close now flows
 * through the consolidated `TillCloseDenominationDialog` in
 * `TillShiftCloseFlow.js`.
 */
import React, { useState } from "react";
import { Coins, Check, Info } from "lucide-react";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function DialogShell({ title, subtitle, icon: Icon, children, testid }) {
  return (
    <div className="fixed inset-0 z-[95] bg-[#0F1A16]/70 backdrop-blur-sm flex items-center justify-center px-6"
         data-testid={testid}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[color:var(--color-primary)]/10 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[color:var(--color-primary)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold">Till</div>
            <h2 className="text-[18px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] leading-tight">{title}</h2>
            {subtitle && <p className="text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1">{subtitle}</p>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Float entry prompt — the one and only till-open UX for cashiers,
 *  supervisors, managers and outlet_admins. Prompted right after PIN
 *  login for anyone with the `settle` permission. */
export function TillFloatPrompt({ staffName, staffRole, onSubmit, onDismiss, busy }) {
  const [float, setFloat] = useState("");
  const val = Number(float);
  const valid = float !== "" && !Number.isNaN(val) && val >= 0;

  return (
    <DialogShell
      testid="pos-till-float-prompt"
      icon={Coins}
      title={`Open till for ${staffName || "you"}`}
      subtitle={<>Enter the opening cash float. This till belongs to <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{staffName || "you"}</strong> alone — every settled tender for this session is attributed to it.</>}>
      <label className="text-[11px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold">
        Opening float (₹)
      </label>
      <input
        type="number" min="0" step="1"
        value={float}
        onChange={(e) => setFloat(e.target.value)}
        placeholder="0"
        autoFocus
        onKeyDown={(e) => { if (e.key === "Enter" && valid && !busy) onSubmit(val); }}
        data-testid="pos-till-float-input"
        className="h-12 rounded-lg border-2 border-[color:var(--color-primary)] bg-[#F4F7F5] px-3 text-[24px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]" />
      <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] p-2.5 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-[1px] shrink-0" />
        <span>
          Set to <strong>0</strong> if you are starting without cash. Concurrent tills at this outlet are supported —
          your figures never mingle with another cashier&apos;s.
        </span>
      </div>
      <div className="flex items-center gap-2">
        {onDismiss && (
          <button type="button" onClick={onDismiss} disabled={busy}
            data-testid="pos-till-float-later"
            className="h-11 flex-1 rounded-lg border border-[color:var(--pos-color-border,#E2E8DF)] text-[color:var(--pos-color-secondary-text,#5C6B64)] text-[13px] font-medium hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] disabled:opacity-40">
            Not now
          </button>
        )}
        <button type="button" onClick={() => onSubmit(val)} disabled={!valid || busy}
          data-testid="pos-till-float-submit"
          className="h-11 flex-[2] rounded-lg bg-[color:var(--color-primary)] text-white text-[14px] font-semibold hover:brightness-110 disabled:opacity-40 flex items-center justify-center gap-2">
          <Check className="w-4 h-4" /> {busy ? "Opening…" : `Open till${valid ? ` · ${inr(val)}` : ""}`}
        </button>
      </div>
      <p className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] text-center">
        Role · <strong className="uppercase tracking-wider">{staffRole || "—"}</strong>
      </p>
    </DialogShell>
  );
}
